/** SUNTRONIX ONE — ONE Concierge (functional orchestration layer).
 *
 *  Every outcome tile + "Ask ONE" flows through a single Concierge
 *  workspace.  The workspace resolves context, calls the backend
 *  /api/concierge/plans/draft endpoint to build a real plan with facts,
 *  recommendations, gaps and record-creation preview, and — on
 *  confirmation — writes an idempotent request linked to the plan.
 *
 *  Backwards compatibility with the pre-refactor "Equip a new employee"
 *  flow is preserved by routing the "equip" outcome to the existing
 *  EquipEmployeeFlow when the user prefers it.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import OneBefore from "@/components/app/OneBefore";
import PlaybookConfidenceBadge from "@/components/app/PlaybookConfidenceBadge";
import {
  UserPlus, Undo2, RefreshCw, Route, Wrench, ArrowUpCircle,
  ShoppingCart, Building2, Users, Recycle, Sparkles, MessageSquare,
  CheckCircle2, AlertTriangle, Clock, ChevronRight, Search, Loader2, ArrowLeft,
  Package,
} from "lucide-react";

const OUTCOMES = [
  { id: "equip", icon: UserPlus,       title: "Equip a new employee",        detail: "Day-1 readiness — device, MDM, accessories, delivery." },
  { id: "recover", icon: Undo2,        title: "Recover an employee's tech",   detail: "Reverse pickup, inspection, warehouse receipt." },
  { id: "replace", icon: RefreshCw,    title: "Replace a device",             detail: "New device dispatched · old device recovered." },
  { id: "move", icon: Route,           title: "Move an employee",             detail: "Location · company · cost centre transfer." },
  { id: "repair", icon: Wrench,        title: "Repair a device",              detail: "Vendor RMA · loaner · service case." },
  { id: "upgrade", icon: ArrowUpCircle,title: "Upgrade an employee",          detail: "Higher-spec device or additional accessory." },
  { id: "order", icon: ShoppingCart,   title: "Order technology",             detail: "Procurement request · quotation · PO." },
  { id: "team", icon: Users,           title: "Equip a team",                 detail: "Bulk outcome for a new office or project." },
  { id: "office", icon: Building2,     title: "Deploy to a new office",       detail: "Multi-employee readiness plan by target date." },
  { id: "refresh", icon: RefreshCw,    title: "Refresh old devices",          detail: "Age · warranty · condition-driven fleet refresh." },
  { id: "dispose", icon: Recycle,      title: "Dispose retired technology",   detail: "Data erasure · ITAD · buyback recovery." },
  { id: "other", icon: MessageSquare,  title: "Ask for something else",       detail: "Free-form outcome — we'll match it to a workflow." },
];

export default function Concierge() {
  const { user } = useAuth?.() || { user: {} };
  const navigate = useNavigate();
  const { planId } = useParams();
  const [ask, setAsk] = useState("");
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    api.get("/concierge/plans", { params: { limit: 6 } })
      .then(({ data }) => setPlans(data.items || []))
      .catch(() => {});
  }, [planId]);

  if (planId) return <PlanWorkspace planId={planId} onBack={() => navigate("/concierge")} />;

  const openOutcome = async (outcomeId) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/concierge/plans/draft", {
        outcome_id: outcomeId, inputs: {}, idempotency_key: `${outcomeId}-${Date.now()}`,
      });
      navigate(`/concierge/plan/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open Concierge workspace");
    } finally {
      setBusy(false);
    }
  };

  const proposePlan = async () => {
    if (!ask.trim() || busy) return;
    setBusy(true);
    try {
      const { data: intent } = await api.post("/concierge/interpret", { text: ask.trim() });
      const outcomeId = intent.outcome_id || "other";
      if (intent.missing?.length) {
        setBanner({
          kind: "info",
          text: `ONE understood '${OUTCOMES.find((o) => o.id === outcomeId)?.title}'. Still need: ${intent.missing.join(", ")}.`,
        });
      }
      const { data: plan } = await api.post("/concierge/plans/draft", {
        outcome_id: outcomeId, inputs: intent.inputs || {}, raw: ask.trim(),
        idempotency_key: `ask-${Date.now()}`,
      });
      navigate(`/concierge/plan/${plan.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ask ONE is temporarily unavailable — pick an outcome above.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1360px] space-y-8" data-testid="concierge">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">SUNTRONIX ONE · Concierge</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tighter mt-1">What would you like to do?</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl leading-relaxed">
          Tell us the outcome — we&apos;ll orchestrate the procurement, configuration, delivery and recovery behind it.
        </p>
      </div>

      {banner && (
        <div className={"rounded-md p-3 text-[13px] " + (banner.kind === "info" ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-slate-50 border border-slate-200")}>
          {banner.text}
        </div>
      )}

      {/* Ask ONE */}
      <div className="bg-slate-900 text-slate-100 rounded-md p-5">
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-amber-400 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Ask ONE
        </div>
        <div className="mt-1 text-lg font-heading text-white">Describe your goal in one sentence.</div>
        <div className="mt-3 flex gap-2">
          <Input
            className="flex-1 h-11 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            placeholder="e.g. Equip 12 new employees joining Mumbai next Monday with MacBook Airs"
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && proposePlan()}
            data-testid="concierge-ask-input"
          />
          <Button
            className="bg-amber-400 text-slate-900 hover:bg-amber-300 h-11 px-5"
            disabled={!ask.trim() || busy}
            onClick={proposePlan}
            data-testid="concierge-ask-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Propose a plan
          </Button>
        </div>
        <div className="mt-2 text-[11px] text-slate-400">ONE never executes on this text alone — it drafts a plan for you to review first.</div>
      </div>

      {/* Outcome menu */}
      <div>
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500 mb-3">Or pick a common outcome</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {OUTCOMES.map((o) => {
            const Icon = o.icon;
            return (
              <button
                key={o.id}
                onClick={() => openOutcome(o.id)}
                disabled={busy}
                data-testid={`concierge-outcome-${o.id}`}
                aria-label={`${o.title} — ${o.detail}`}
                className="text-left bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 hover:-translate-y-px hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300 transition-[transform,box-shadow,border-color] flex gap-3 group disabled:opacity-60 disabled:cursor-wait"
              >
                <div className="h-9 w-9 rounded-md bg-slate-50 border border-slate-100 grid place-items-center shrink-0 group-hover:bg-amber-50 group-hover:border-amber-100 transition-colors">
                  <Icon className="h-4 w-4 text-slate-700" strokeWidth={1.6} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 leading-snug">{o.title}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-relaxed">{o.detail}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-700 transition-colors shrink-0" strokeWidth={1.5} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent plans */}
      {plans.length > 0 && (
        <div data-testid="concierge-recent">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500 mb-3">Recent outcomes</div>
          <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
            {plans.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/concierge/plan/${p.id}`)}
                data-testid={`concierge-plan-${p.id}`}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 text-[13px]"
              >
                <StatusDot status={p.status} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{p.outcome_title}</div>
                  <div className="text-[11px] text-slate-500">
                    {p.status} · {new Date(p.created_at).toLocaleString()}
                    {p.raw ? ` · "${p.raw.slice(0, 60)}"` : ""}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PlanWorkspace({ planId, onBack }) {
  const { user } = useAuth?.() || { user: {} };
  const [plan, setPlan] = useState(null);
  const [inputs, setInputs] = useState({});
  const [empQuery, setEmpQuery] = useState("");
  const [empMatches, setEmpMatches] = useState([]);
  const [rebuilding, setRebuilding] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [idem] = useState(`confirm-${planId}`);

  useEffect(() => {
    api.get(`/concierge/plans/${planId}`).then(({ data }) => {
      setPlan(data);
      setInputs(data.inputs || {});
    }).catch((e) => toast.error(e.response?.data?.detail || "Plan not found"));
  }, [planId]);

  const searchEmployees = async (q) => {
    setEmpQuery(q);
    if (!q || q.length < 2) { setEmpMatches([]); return; }
    try {
      const r = await api.get("/employees", { params: { q, limit: 6 } });
      setEmpMatches(r.data.items || []);
    } catch { /* ignore */ }
  };

  const rebuild = async (nextInputs) => {
    setRebuilding(true);
    try {
      const { data } = await api.post("/concierge/plans/draft", {
        outcome_id: plan.outcome_id, inputs: nextInputs, raw: plan.raw,
      });
      setPlan(data);
    } finally {
      setRebuilding(false);
    }
  };

  const confirmPlan = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/concierge/plans/${planId}/confirm`, { idempotency_key: idem });
      setPlan(data);
      toast.success("Plan started. ONE is orchestrating the workflows.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm plan");
    } finally {
      setConfirming(false);
    }
  };

  if (!plan) return <div className="p-6 text-slate-500 text-sm" data-testid="plan-loading">Loading plan…</div>;

  const body = plan.body || {};
  const facts = body.facts || {};
  const inv = facts.inventory || {};
  const gaps = body.gaps || [];
  const risks = body.risks || [];
  const recs = body.recommendations || [];
  const creates = body.creates || [];
  const oneBeforeAction = (
    plan.outcome_id === "order" || plan.outcome_id === "team" || plan.outcome_id === "office"
    || plan.outcome_id === "equip" || plan.outcome_id === "upgrade"
  ) ? "buy" : plan.outcome_id === "replace" ? "replace"
    : plan.outcome_id === "dispose" ? "recycle" : null;

  const canConfirm = gaps.length === 0 && plan.status !== "IN_PROGRESS" && plan.status !== "COMPLETE";

  return (
    <div className="max-w-[1360px] space-y-6" data-testid="plan-workspace">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="plan-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Concierge</Button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Outcome</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">{plan.outcome_title}</h1>
          {facts.customer_standard && Object.keys(facts.customer_standard).length > 0 && (
            <div className="mt-1.5">
              <PlaybookConfidenceBadge standard={facts.customer_standard} testId="plan-playbook-badge" />
            </div>
          )}
        </div>
        <StatusPill status={plan.status} />
      </div>

      {oneBeforeAction && (
        <div data-testid={`plan-onebefore-${oneBeforeAction}`}>
          <OneBefore action={oneBeforeAction} masterCompanyId={user?.master_company_id} />
        </div>
      )}

      {plan.raw && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-[13px] text-slate-700">
          <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mr-2">Your ask</span>
          &quot;{plan.raw}&quot;
        </div>
      )}

      {/* Context — inputs the plan needs */}
      <Section title="Context">
        <div className="grid md:grid-cols-2 gap-3">
          {(["equip", "recover", "replace", "move", "upgrade"].includes(plan.outcome_id)) && (
            <div>
              <Label className="text-xs">Employee</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input className="h-10 pl-9" placeholder="Search by name, email or code…" value={empQuery} onChange={(e) => searchEmployees(e.target.value)} data-testid="plan-emp-search" />
              </div>
              {body.resolved?.employee && (
                <div className="mt-1 text-[12px] text-emerald-800">Selected: <b>{body.resolved.employee.name}</b> · {body.resolved.employee.employee_code}</div>
              )}
              {empMatches.length > 0 && (
                <div className="mt-1 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-56 overflow-auto">
                  {empMatches.map((e) => (
                    <button key={e.id} onClick={() => { const nxt = { ...inputs, employee_query: e.name || e.email }; setInputs(nxt); rebuild(nxt); setEmpMatches([]); setEmpQuery(""); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-[13px]" data-testid={`plan-emp-${e.id}`}>
                      <div className="font-medium text-slate-900">{e.name}</div>
                      <div className="text-[11px] text-slate-500">{e.email} · {e.employee_code}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {["order", "team", "office", "equip", "upgrade"].includes(plan.outcome_id) && (
            <>
              <SmallField label="Quantity" testid="plan-quantity">
                <Input type="number" min="1" className="h-10" value={inputs.quantity || ""} onChange={(e) => { const nxt = { ...inputs, quantity: Number(e.target.value) || 1 }; setInputs(nxt); }} onBlur={() => rebuild(inputs)} />
              </SmallField>
              <SmallField label="Product / profile" testid="plan-product">
                <Input className="h-10" placeholder="MacBook Pro 14 / Latitude 7440 …" value={inputs.product || ""} onChange={(e) => setInputs({ ...inputs, product: e.target.value })} onBlur={() => rebuild(inputs)} />
              </SmallField>
              <SmallField label="Location" testid="plan-location">
                <Input className="h-10" placeholder="Mumbai / London / SG …" value={inputs.location || ""} onChange={(e) => setInputs({ ...inputs, location: e.target.value })} onBlur={() => rebuild(inputs)} />
              </SmallField>
              <SmallField label="Required by" testid="plan-when">
                <Input type="date" className="h-10" value={inputs.when || ""} onChange={(e) => { const nxt = { ...inputs, when: e.target.value }; setInputs(nxt); rebuild(nxt); }} />
              </SmallField>
            </>
          )}
          {plan.outcome_id === "replace" && (
            <SmallField label="Reason" testid="plan-reason">
              <Input className="h-10" placeholder="breakdown / damage / performance / lost / refresh" value={inputs.reason || ""} onChange={(e) => setInputs({ ...inputs, reason: e.target.value })} onBlur={() => rebuild(inputs)} />
            </SmallField>
          )}
          {plan.outcome_id === "repair" && (
            <>
              <SmallField label="Asset ID" testid="plan-asset-id">
                <Input className="h-10" placeholder="Paste asset ID…" value={inputs.asset_id || ""} onChange={(e) => setInputs({ ...inputs, asset_id: e.target.value })} onBlur={() => rebuild(inputs)} />
              </SmallField>
              <SmallField label="Issue" testid="plan-issue">
                <Input className="h-10" value={inputs.issue || ""} onChange={(e) => setInputs({ ...inputs, issue: e.target.value })} onBlur={() => rebuild(inputs)} />
              </SmallField>
            </>
          )}
        </div>
        {rebuilding && <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Rebuilding plan…</div>}
      </Section>

      {/* Facts */}
      <Section title="ONE found — inventory facts" testid="plan-facts">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Fact label="Redeployment ready" value={inv.redeployment_ready ?? "—"} tone="emerald" />
          <Fact label="Ready to deploy" value={inv.ready_to_deploy ?? "—"} />
          <Fact label="New stock" value={inv.new_stock ?? "—"} />
          <Fact label="Reserved" value={inv.reserved ?? "—"} />
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Fact label="Customer-owned total" value={inv.customer_owned_total ?? "—"} tone="slate" />
          <Fact label="In repair" value={inv.in_repair ?? "—"} />
          <Fact label="Quantity requested" value={body.quantity ?? 1} />
          <Fact label="Supply shortfall" value={body.shortfall ?? 0} tone={body.shortfall > 0 ? "amber" : "emerald"} />
        </div>
      </Section>

      {/* Gaps + Risks */}
      {gaps.length > 0 && (
        <Section title="Missing information" testid="plan-gaps">
          <ul className="space-y-1 text-[13px]">
            {gaps.map((g, i) => (<li key={i} className="flex gap-2 text-amber-900"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {g}</li>))}
          </ul>
        </Section>
      )}
      {risks.length > 0 && (
        <Section title="Risks" testid="plan-risks">
          <ul className="space-y-1 text-[13px] text-slate-700">
            {risks.map((r, i) => (<li key={i} className="flex gap-2"><Clock className="h-4 w-4 mt-0.5 shrink-0 text-slate-500" /> {r}</li>))}
          </ul>
        </Section>
      )}

      {/* Recommendations */}
      <Section title="Recommended plan" testid="plan-recs">
        {recs.length === 0 ? (
          <div className="text-[13px] text-slate-500">No recommendations yet — provide the missing context above.</div>
        ) : (
          <ol className="space-y-2">
            {recs.map((r, i) => (
              <li key={i} className="flex items-start gap-3 border border-slate-200 rounded-md p-3">
                <div className="h-6 w-6 rounded-full bg-slate-900 text-white text-[11px] font-semibold grid place-items-center shrink-0">{i + 1}</div>
                <div className="flex-1">
                  <div className="text-[13.5px] font-medium text-slate-900">{r.title}</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">{r.reason}</div>
                </div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 tabular-nums">×{r.count}</div>
              </li>
            ))}
          </ol>
        )}
      </Section>

      {/* This will create */}
      <Section title="This will create when confirmed" testid="plan-creates">
        {creates.length === 0 ? (
          <div className="text-[13px] text-slate-500">Nothing yet — fill in the context above.</div>
        ) : (
          <ul className="space-y-1 text-[13px] text-slate-700">
            {creates.map((c, i) => (<li key={i} className="flex gap-2"><Package className="h-4 w-4 mt-0.5 shrink-0 text-slate-500" /> {c.count} × <b>{c.type.replace(/_/g, " ")}</b>{c.purpose ? ` — ${c.purpose}` : ""}</li>))}
          </ul>
        )}
      </Section>

      {/* Linked records after confirm */}
      {(plan.linked_records || []).length > 0 && (
        <Section title="Linked records" testid="plan-linked">
          <ul className="text-[13px] font-mono text-slate-700">
            {plan.linked_records.map((r, i) => <li key={i}>• {r.kind}: {r.id}</li>)}
          </ul>
        </Section>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 sticky bottom-4 bg-white/90 backdrop-blur rounded-md p-3 border border-slate-200 shadow-sm">
        <Button variant="outline" onClick={onBack} data-testid="plan-cancel">Cancel</Button>
        <div className="ml-auto flex items-center gap-2">
          {plan.status === "IN_PROGRESS" && (
            <div className="text-[12px] text-emerald-800 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Plan started</div>
          )}
          <Button className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50" disabled={!canConfirm || confirming} onClick={confirmPlan} data-testid="plan-confirm">
            {confirming ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
            Confirm &amp; start
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function SmallField({ label, testid, children }) {
  return <div data-testid={testid}><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

function Fact({ label, value, tone = "slate" }) {
  const toneCls = tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="border border-slate-100 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={"mt-0.5 text-[18px] font-semibold tabular-nums " + toneCls}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    DRAFT: "bg-slate-100 text-slate-700",
    READY_FOR_REVIEW: "bg-amber-50 text-amber-800 border border-amber-100",
    MISSING_INFORMATION: "bg-amber-50 text-amber-800 border border-amber-100",
    IN_PROGRESS: "bg-emerald-50 text-emerald-800 border border-emerald-100",
    COMPLETE: "bg-emerald-100 text-emerald-800",
  };
  const cls = map[status] || "bg-slate-100 text-slate-700";
  return <span className={`text-[11px] uppercase tracking-[0.14em] px-2 py-1 rounded ${cls}`} data-testid="plan-status">{(status || "").replace(/_/g, " ")}</span>;
}

function StatusDot({ status }) {
  const c = status === "IN_PROGRESS" ? "bg-emerald-500" : status === "COMPLETE" ? "bg-emerald-600" : status === "MISSING_INFORMATION" ? "bg-amber-500" : "bg-slate-400";
  return <span className={`h-2 w-2 rounded-full ${c} inline-block`} aria-hidden="true" />;
}
